import {
  Bell,
  FileSearch,
  type LucideIcon,
  Radar,
  Search,
  Send,
  Settings,
  Star,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { motion, type Variants } from "motion/react";

import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const frameVariants: Variants = {
  hidden: { opacity: 0, y: 56, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: EASE, delay: 0.3 } },
};

const bodyVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.55 } },
};

const pieceVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

interface DashboardPreviewProps {
  /**
   * Overrides the outer wrapper sizing. Pass `max-w-none px-0` when embedding
   * the preview somewhere that should not constrain width (e.g. the bento
   * "the code is yours" card), otherwise it defaults to the hero layout.
   */
  className?: string;
}

/**
 * Token-styled peek at the Intelligence Hub behind the landing. Built entirely
 * from theme tokens so it stays on-theme with no asset pipeline, but filled
 * with realistic research-desk content instead of empty skeletons. Sits below
 * the hero in a 16:10 browser frame so it reads like a screenshot of the
 * product.
 */
export const DashboardPreview = ({ className }: DashboardPreviewProps = {}) => {
  return (
    <div className={cn("mx-auto w-full max-w-[1160px] px-4", className)}>
      <motion.div
        variants={frameVariants}
        initial="hidden"
        animate="show"
        className="overflow-hidden rounded-2xl border border-border/60 bg-card/40 p-2 shadow-2xl backdrop-blur"
      >
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border/60 bg-background shadow-lg lg:h-[640px]">
          <WindowChrome />

          <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[200px_1fr]">
            <Sidebar />
            <DashboardBody />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

function WindowChrome() {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-4 py-2.5">
      <span className="size-3 rounded-full bg-destructive/70" />
      <span className="size-3 rounded-full bg-primary/60" />
      <span className="size-3 rounded-full bg-secondary/70" />
      <div className="ml-3 flex h-7 flex-1 items-center gap-2 rounded-md bg-muted/50 px-3 text-xs text-muted-foreground">
        <span className="size-3 rounded-full border border-muted-foreground/40" />
        app.{m.app_title().toLowerCase().replace(/\s+/g, "")}.com/research
      </div>
    </div>
  );
}

type NavItem = { icon: LucideIcon; label: string; active?: boolean; badge?: string };

const NAV_ITEMS: NavItem[] = [
  { icon: FileSearch, label: m.landing_preview_nav_research(), active: true },
  { icon: Radar, label: m.landing_preview_nav_signals(), badge: "4" },
  { icon: Users, label: m.landing_preview_nav_clients() },
  { icon: Send, label: m.landing_preview_nav_distribution() },
  { icon: Settings, label: m.landing_preview_nav_settings() },
];

function Sidebar() {
  return (
    <aside className="hidden flex-col gap-1 border-r border-border/60 bg-muted/30 p-3 md:flex">
      <div className="flex items-center gap-2 px-2 py-2">
        <span className="flex size-7 items-center justify-center rounded-lg bg-primary/15 text-xs font-semibold text-primary">
          {m.app_title().charAt(0)}
        </span>
        <span className="text-sm font-semibold text-foreground">{m.app_title()}</span>
      </div>

      <nav className="mt-2 flex flex-col gap-1">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                item.active ? "bg-primary/10 text-primary" : "text-muted-foreground",
              )}
            >
              <Icon className="size-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                  {item.badge}
                </span>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto rounded-xl border border-border/60 bg-card/60 p-3">
        <div className="text-xs font-medium text-foreground">{m.landing_preview_coverage_label()}</div>
        <div className="mt-1 text-xs text-muted-foreground">{m.landing_preview_coverage_hint()}</div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-[88%] rounded-full bg-primary" />
        </div>
      </div>
    </aside>
  );
}

type Stat = {
  label: string;
  value: string;
  delta: string;
  trend: "up" | "down";
  points: readonly number[];
};

const STATS: readonly Stat[] = [
  {
    label: m.landing_preview_stat_notes(),
    value: "1,240",
    delta: "+12.4%",
    trend: "up",
    points: [4, 6, 5, 8, 7, 9, 11],
  },
  {
    label: m.landing_preview_stat_queries(),
    value: "8,412",
    delta: "+18.1%",
    trend: "up",
    points: [3, 4, 6, 5, 7, 8, 9],
  },
  {
    label: m.landing_preview_stat_first_calls(),
    value: "312",
    delta: "+9.6%",
    trend: "up",
    points: [4, 5, 5, 6, 7, 8, 9],
  },
  {
    label: m.landing_preview_stat_uptime(),
    value: "99.98%",
    delta: "+0.02%",
    trend: "up",
    points: [9, 9, 10, 10, 10, 10, 10],
  },
];

const ENGAGEMENT_POINTS = [20, 32, 28, 44, 38, 56, 52, 68, 62, 78, 74, 92];
const ENGAGEMENT_MONTHS = [
  m.better_studio_month_jan(),
  m.better_studio_month_feb(),
  m.better_studio_month_mar(),
  m.better_studio_month_apr(),
  m.better_studio_month_may(),
  m.better_studio_month_jun(),
  m.better_studio_month_jul(),
  m.better_studio_month_aug(),
  m.better_studio_month_sep(),
  m.better_studio_month_oct(),
  m.better_studio_month_nov(),
  m.better_studio_month_dec(),
];

function DashboardBody() {
  return (
    <motion.div variants={bodyVariants} className="flex min-h-0 flex-col gap-3 overflow-hidden p-3 md:p-4">
      <motion.div variants={pieceVariants}>
        <TopBar />
      </motion.div>

      <motion.div variants={pieceVariants} className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {STATS.map((stat) => (
          <StatCard key={stat.label} stat={stat} />
        ))}
      </motion.div>

      <motion.div variants={pieceVariants} className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[1.5fr_1fr]">
        <EngagementChart />
        <ActivityFeed />
      </motion.div>
    </motion.div>
  );
}

function TopBar() {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground md:text-base">{m.landing_preview_nav_research()}</h3>
        <p className="text-xs text-muted-foreground">{m.landing_preview_subtitle()}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="hidden items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground md:flex">
          <Search className="size-3.5" />
          <span>{m.landing_preview_search()}</span>
        </div>
        <span className="flex size-8 items-center justify-center rounded-lg border border-border/60 text-muted-foreground">
          <Bell className="size-4" />
        </span>
        <span className="flex size-8 items-center justify-center rounded-full bg-secondary/70 text-xs font-semibold text-secondary-foreground">
          AR
        </span>
      </div>
    </div>
  );
}

function StatCard({ stat }: { stat: Stat }) {
  const Icon = stat.trend === "up" ? TrendingUp : TrendingDown;

  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-3.5">
      <div className="text-xs text-muted-foreground">{stat.label}</div>
      <div className="mt-1.5 text-lg font-semibold text-foreground md:text-xl">{stat.value}</div>
      <div className="mt-2 flex items-center gap-1.5">
        <span
          className={cn(
            "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium",
            stat.trend === "up" ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive",
          )}
        >
          <Icon className="size-3" />
          {stat.delta}
        </span>
        <Sparkline points={stat.points} trend={stat.trend} />
      </div>
    </div>
  );
}

function Sparkline({ points, trend }: { points: readonly number[]; trend: "up" | "down" }) {
  const w = 64;
  const h = 20;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - ((p - min) / span) * h}`).join(" ");
  const stroke = trend === "up" ? "var(--primary)" : "var(--destructive)";

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" className="ml-auto">
      <path d={d} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EngagementChart() {
  const w = 520;
  const h = 180;
  const pad = 8;
  const max = 100;
  const step = (w - pad * 2) / (ENGAGEMENT_POINTS.length - 1);
  const toXY = (p: number, i: number) => [pad + i * step, h - pad - (p / max) * (h - pad * 2)] as const;
  const line = ENGAGEMENT_POINTS.map((p, i) => `${i === 0 ? "M" : "L"} ${toXY(p, i)[0]} ${toXY(p, i)[1]}`).join(" ");
  const area = `${line} L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`;

  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-border/60 bg-card/60 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">{m.landing_preview_engagement_title()}</div>
          <div className="text-xs text-muted-foreground">{m.landing_preview_engagement_period()}</div>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold text-foreground">8,412</div>
          <div className="inline-flex items-center gap-0.5 text-xs font-medium text-primary">
            <TrendingUp className="size-3" />
            +18.2%
          </div>
        </div>
      </div>

      <div className="relative mt-3 flex-1">
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-full w-full">
          <defs>
            <linearGradient id="engagement-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.28} />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((g) => (
            <line
              key={g}
              x1={pad}
              x2={w - pad}
              y1={h * g}
              y2={h * g}
              stroke="var(--border)"
              strokeOpacity={0.5}
              strokeDasharray="3 4"
            />
          ))}
          <path d={area} fill="url(#engagement-area)" />
          <path
            d={line}
            fill="none"
            stroke="var(--primary)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
        {ENGAGEMENT_MONTHS.map((month) => (
          <span key={month}>{month}</span>
        ))}
      </div>
    </div>
  );
}

type Activity = { initials: string; name: string; action: string; time: string; tone: string };

const ACTIVITY: readonly Activity[] = [
  {
    initials: "SW",
    name: "S. Whitfield",
    action: m.landing_preview_activity_queried(),
    time: "2m",
    tone: "bg-primary/15 text-primary",
  },
  {
    initials: "JO",
    name: "J. Okafor",
    action: m.landing_preview_activity_matched(),
    time: "1h",
    tone: "bg-secondary/70 text-secondary-foreground",
  },
  {
    initials: "RM",
    name: "R. Mehta",
    action: m.landing_preview_activity_briefed(),
    time: "3h",
    tone: "bg-color-1/20 text-foreground",
  },
  {
    initials: "AL",
    name: "A. Lindqvist",
    action: m.landing_preview_activity_connected(),
    time: "5h",
    tone: "bg-color-3/20 text-foreground",
  },
];

function ActivityFeed() {
  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-border/60 bg-card/60 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">{m.landing_preview_activity_title()}</div>
        <Star className="size-4 text-muted-foreground" />
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {ACTIVITY.map((row) => (
          <div key={row.name} className="flex items-center gap-3">
            <span
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                row.tone,
              )}
            >
              {row.initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-foreground">
                <span className="font-medium">{row.name}</span>{" "}
                <span className="text-muted-foreground">{row.action}</span>
              </p>
            </div>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{row.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
